import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { StatusBadge, ApprovalStatusBadge, WorkStatusBadge } from './AdminBadges';
import { RemoveFromQueueModal, MoveToProductionModal, FinishWorkModal } from './AdminAppHeaderModals';
import { captureError } from '@/utils/errorHandler';

export default function AdminAppHeader({ app, appId, onStatusChange, showToast, onTransferClick, authFetch, onUpdate }) {
  const appData = app;
  const id = appId;
  const navigate = useNavigate();
  const [editingName, setEditingName] = useState(false);
  const [appName, setAppName] = useState(appData?.name || '');
  const [savingName, setSavingName] = useState(false);
  const [editingDevUrl, setEditingDevUrl] = useState(false);
  const [devUrl, setDevUrl] = useState(appData?.devUrl || '');
  const [savingDevUrl, setSavingDevUrl] = useState(false);
  const [editingLiveUrl, setEditingLiveUrl] = useState(false);
  const [liveUrl, setLiveUrl] = useState(appData?.appUrl || '');
  const [savingLiveUrl, setSavingLiveUrl] = useState(false);
  const [showFinishWorkModal, setShowFinishWorkModal] = useState(false);
  const [showMoveToProductionModal, setShowMoveToProductionModal] = useState(false);
  const [showRemoveFromQueueModal, setShowRemoveFromQueueModal] = useState(false);
  const [startingWork, setStartingWork] = useState(false);
  const [stoppingWork, setStoppingWork] = useState(false);
  
  const isApproved = appData?.approvalStatus === 'approved';

  // Payment status checks - check for truthy timestamp values
  // Using != null to catch both null and undefined (loose equality checks both)
  const hasQuote = appData?.quotedAt != null;
  const depositPaid = appData?.depositPaidAt != null;
  const finalPaid = appData?.finalPaidAt != null;
  
  // Check if payment requirements are met to start work
  const canStartWork = () => {
    return hasQuote && depositPaid;
  };

  const paymentStatusMessage = () => {
    if (hasQuote) {
      if (!depositPaid) {
        return 'Deposit not yet paid';
      }
      if (!finalPaid && isApproved) {
        return 'Work complete - Final payment pending';
      }
      return null;
    }
    return null;
  };

  // Sync name, dev URL and live URL when app updates
  useEffect(() => {
    if (appData?.name !== undefined) {
      setAppName(appData.name || '');
    }
    if (appData?.devUrl !== undefined) {
      setDevUrl(appData.devUrl || '');
    }
    if (appData?.appUrl !== undefined) {
      setLiveUrl(appData.appUrl || '');
    }
  }, [appData?.name, appData?.devUrl, appData?.appUrl]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          {editingName ? (
            <div className="flex items-center gap-2 mb-2">
              <input
                type="text"
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                placeholder="App name"
                className="flex-1 px-3 py-2 text-xl font-bold border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                autoFocus
              />
              <button
                onClick={async () => {
                  if (!appName.trim()) {
                    showToast('App name cannot be empty', 'error');
                    return;
                  }
                  setSavingName(true);
                  try {
                    const response = await authFetch('/api/apps/update', {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        id: id,
                        name: appName.trim(),
                      }),
                    });
                    if (response.ok) {
                      showToast('✅ App name updated', 'success');
                      setEditingName(false);
                      if (onUpdate) onUpdate();
                    } else {
                      const error = await response.json();
                      throw new Error(error.error || 'Failed to update');
                    }
                  } catch (error) {
                    captureError(error, {
                      action: 'handleSaveName',
                      component: 'AdminAppHeader',
                      extra: { appId: id, appName },
                    });
                    showToast(error.message || 'Failed to update app name', 'error');
                  } finally {
                    setSavingName(false);
                  }
                }}
                disabled={savingName}
                className="px-3 py-2 text-xs bg-zapt-purple text-white rounded hover:bg-zapt-purple/90 disabled:opacity-50"
              >
                {savingName ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => {
                  setAppName(appData?.name || '');
                  setEditingName(false);
                }}
                className="px-3 py-2 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 mb-2">
              <h1 className="text-2xl font-bold text-gray-900">{appData?.name}</h1>
              <button
                onClick={() => setEditingName(true)}
                className="text-xs text-zapt-purple hover:text-zapt-purple/80 underline"
              >
                Edit
              </button>
            </div>
          )}
          {appData?.userEmail && (
            <p className="text-sm text-gray-500 mb-3">
              <span className="font-medium">User Email:</span> {appData.userEmail}
            </p>
          )}
          <div className="flex items-center gap-4 flex-wrap">
            <StatusBadge status={appData?.status} />
            <WorkStatusBadge workStatus={appData?.workStatus} />
            <ApprovalStatusBadge approvalStatus={appData?.approvalStatus} />
            
            {/* Status Selector */}
            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
              {['draft', 'live', 'archived'].map((status) => (
                <button
                  key={status}
                  onClick={() => onStatusChange(status)}
                  disabled={status === appData?.status}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    status === appData?.status
                      ? 'bg-white text-gray-900 shadow-sm cursor-default'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {status === 'live' && '✅ '}
                  {status === 'draft' && '📋 '}
                  {status === 'archived' && '📦 '}
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>


      {/* Live URL Section */}
      <div className="mt-3 pt-3 border-t border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-gray-500">Live URL:</p>
          {appData?.liveSince && (
            <p className="text-xs text-gray-400">
              Live since {new Date(appData.liveSince).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </p>
          )}
        </div>
        {editingLiveUrl ? (
          <div className="flex items-center gap-2">
            <input
              type="url"
              value={liveUrl}
              onChange={(e) => setLiveUrl(e.target.value)}
              placeholder="https://example.com"
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <button
              onClick={async () => {
                setSavingLiveUrl(true);
                try {
                  const response = await authFetch('/api/apps/update', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      id: id,
                      appUrl: liveUrl.trim() || null,
                    }),
                  });
                  if (response.ok) {
                    showToast('✅ Live URL updated', 'success');
                    setEditingLiveUrl(false);
                    if (onUpdate) onUpdate();
                  } else {
                    const error = await response.json();
                    throw new Error(error.error || 'Failed to update');
                  }
                } catch (error) {
                  captureError(error, {
                    action: 'handleSaveLiveUrl',
                    component: 'AdminAppHeader',
                    extra: { appId: id, liveUrl },
                  });
                  showToast(error.message || 'Failed to update live URL', 'error');
                } finally {
                  setSavingLiveUrl(false);
                }
              }}
              disabled={savingLiveUrl}
              className="px-3 py-2 text-xs bg-zapt-purple text-white rounded hover:bg-zapt-purple/90 disabled:opacity-50"
            >
              {savingLiveUrl ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => {
                setLiveUrl(appData?.appUrl || '');
                setEditingLiveUrl(false);
              }}
              className="px-3 py-2 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {liveUrl ? (
              <>
                <a
                  href={liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-700 underline"
                >
                  {liveUrl}
                </a>
                <button
                  onClick={() => setEditingLiveUrl(true)}
                  className="text-xs text-zapt-purple hover:text-zapt-purple/80 underline"
                >
                  Edit
                </button>
              </>
            ) : (
              <>
                <span className="text-sm text-gray-500">No live URL set</span>
                <button
                  onClick={() => setEditingLiveUrl(true)}
                  className="text-xs text-zapt-purple hover:text-zapt-purple/80 underline"
                >
                  + Add Live URL
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Dev URL Section */}
      <div className="mt-3 pt-3 border-t border-gray-200">
        <p className="text-xs font-medium text-gray-500 mb-2">Dev URL:</p>
          {editingDevUrl ? (
            <div className="flex items-center gap-2">
              <input
                type="url"
                value={devUrl}
                onChange={(e) => setDevUrl(e.target.value)}
                placeholder="https://dev.example.com"
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <button
                onClick={async () => {
                  setSavingDevUrl(true);
                  try {
                    const response = await authFetch('/api/apps/update', {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        id: id,
                        devUrl: devUrl.trim() || null,
                      }),
                    });
                    if (response.ok) {
                      showToast('✅ Dev URL updated', 'success');
                      setEditingDevUrl(false);
                      if (onUpdate) onUpdate();
                    } else {
                      const error = await response.json();
                      throw new Error(error.error || 'Failed to update');
                    }
                  } catch (error) {
                    captureError(error, {
                      action: 'handleSaveDevUrl',
                      component: 'AdminAppHeader',
                      extra: { appId: id, devUrl },
                    });
                    showToast(error.message || 'Failed to update dev URL', 'error');
                  } finally {
                    setSavingDevUrl(false);
                  }
                }}
                disabled={savingDevUrl}
                className="px-3 py-2 text-xs bg-zapt-purple text-white rounded hover:bg-zapt-purple/90 disabled:opacity-50"
              >
                {savingDevUrl ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => {
                  setDevUrl(appData?.devUrl || '');
                  setEditingDevUrl(false);
                }}
                className="px-3 py-2 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {devUrl ? (
                <>
                  <a
                    href={devUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700 underline"
                  >
                    {devUrl}
                  </a>
                  <button
                    onClick={() => setEditingDevUrl(true)}
                    className="text-xs text-zapt-purple hover:text-zapt-purple/80 underline"
                  >
                    Edit
                  </button>
                </>
              ) : (
                <>
                  <span className="text-sm text-gray-500">No dev URL set</span>
                  <button
                    onClick={() => setEditingDevUrl(true)}
                    className="text-xs text-zapt-purple hover:text-zapt-purple/80 underline"
                  >
                    + Add Dev URL
                  </button>
                </>
              )}
            </div>
          )}
        </div>

      {/* Tags Display */}
      {appData?.tags && appData.tags.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs font-medium text-gray-500 mb-2">Tags:</p>
          <div className="flex flex-wrap gap-2">
            {appData.tags.map((tag, idx) => (
              <span
                key={idx}
                className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}


      {/* Workflow Actions */}
      {appData?.workStatus === 'in_progress' && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs font-medium text-gray-500 mb-2">Workflow:</p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowFinishWorkModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
            >
              ✅ Finish Work & Request Approval
            </button>
          </div>
        </div>
      )}

      {/* Move to Production (when approved) */}
      {isApproved && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs font-medium text-gray-500 mb-2">Production:</p>
          <button
            onClick={() => setShowMoveToProductionModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            🚀 Move to Production
          </button>
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex gap-2 flex-wrap pt-4 border-t border-gray-200">
        <Link
          to={`/admin-portal/errors?appId=${id}`}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
        >
          🚨 View Errors
        </Link>
        
        {onTransferClick && (
          <button
            onClick={onTransferClick}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
          >
            🔄 Transfer App
          </button>
        )}
        
        {/* Start Work button - shows when app is in queue (needs_work) */}
        {appData?.workStatus === 'needs_work' && (
          <button
            onClick={async () => {
              setStartingWork(true);
              try {
                const response = await authFetch('/api/admin/app-workflow', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    appId: id,
                    action: 'start',
                  }),
                });
                
                if (response.ok) {
                  showToast('Started working on app', 'success');
                  if (onUpdate) onUpdate();
                } else {
                  const error = await response.json();
                  throw new Error(error.error || 'Failed to start work');
                }
              } catch (error) {
                captureError(error, {
                  action: 'handleStartWork',
                  component: 'AdminAppHeader',
                  extra: { appId: id },
                });
                showToast(error.message || 'Failed to start work', 'error');
              } finally {
                setStartingWork(false);
              }
            }}
            disabled={startingWork}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
          >
            {startingWork ? 'Starting...' : '🔨 Start Work'}
          </button>
        )}

        {/* Stop Work button - shows when app is being worked on (in_progress) */}
        {appData?.workStatus === 'in_progress' && (
          <button
            onClick={async () => {
              setStoppingWork(true);
              try {
                const response = await authFetch('/api/admin/app-workflow', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    appId: id,
                    action: 'stop',
                  }),
                });
                
                if (response.ok) {
                  showToast('Stopped working - app moved to back of queue', 'success');
                  if (onUpdate) onUpdate();
                } else {
                  const error = await response.json();
                  throw new Error(error.error || 'Failed to stop work');
                }
              } catch (error) {
                captureError(error, {
                  action: 'handleStopWork',
                  component: 'AdminAppHeader',
                  extra: { appId: id },
                });
                showToast(error.message || 'Failed to stop work', 'error');
              } finally {
                setStoppingWork(false);
              }
            }}
            disabled={stoppingWork}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium disabled:opacity-50"
          >
            {stoppingWork ? 'Stopping...' : '⏸️ Stop Work'}
          </button>
        )}

        {/* Remove from Queue button - shows when app is in queue (needs_work) */}
        {appData?.workStatus === 'needs_work' && (
          <button
            onClick={() => setShowRemoveFromQueueModal(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
          >
            ❌ Remove from Queue
          </button>
        )}
      </div>

      {/* Modals */}
      <RemoveFromQueueModal
        isOpen={showRemoveFromQueueModal}
        onClose={() => setShowRemoveFromQueueModal(false)}
        appId={id}
        appName={appData?.name}
        authFetch={authFetch}
        showToast={showToast}
        onUpdate={onUpdate}
      />

      <MoveToProductionModal
        isOpen={showMoveToProductionModal}
        onClose={() => setShowMoveToProductionModal(false)}
        appId={id}
        devUrl={appData?.devUrl}
        authFetch={authFetch}
        showToast={showToast}
        onUpdate={onUpdate}
      />

      <FinishWorkModal
        isOpen={showFinishWorkModal}
        onClose={() => setShowFinishWorkModal(false)}
        appId={id}
        appName={appData?.name}
        appData={appData}
        authFetch={authFetch}
        showToast={showToast}
        onUpdate={onUpdate}
      />
    </div>
  );
}

